import { endSession } from "@/lib/endSession.ts";
import { resolveLoginReturnNavigation } from "@/views/Auth.tsx";

/** Where a passwordless sign-in goes once it holds a session. */
export type SignInLanding = { threadId: number | null };

/**
 * Finish a passwordless sign-in: tell any tab still sitting on the sign-in
 * page, then navigate this one. The session cookie is set; a full navigation
 * picks it up, and whatever identity this browser held before is replaced, so
 * its storage ends with it.
 *
 * THE BROADCAST IS WHAT THIS PAGE OWES THE TAB THAT OPENED IT. Google sign-in
 * opens in a new tab, so a consent cancelled into a passwordless door leaves
 * the original tab on `/auth`. `OAuthResult` notifies it on every other path,
 * and these pages are among them — without it a user can finish signing up and
 * still be looking at the sign-in screen. BroadcastChannel does not deliver to
 * the sender, so an ordinary same-tab visit from `/auth` posts to nobody.
 *
 * Both tabs are sent to the same place, rather than each resolving its own, so
 * they cannot disagree about where this signup ended.
 */
export const landAfterSignIn = (
  { threadId }: SignInLanding,
  returnTo: string | null,
  provider: "phone" | "whatsapp",
) => {
  const destination = returnTo ?? (threadId ? `/threads/${threadId}` : "/");

  const channel = new BroadcastChannel("oauth_result");
  channel.postMessage({ provider, success: true, flow: "login", returnTo: destination });
  channel.close();

  const resolved = resolveLoginReturnNavigation(destination);
  endSession(resolved.kind === "external" ? resolved.url : resolved.path);
};
